import { createContext } from "react";

import {
  User,
  UserGroup,
  OrganizedUserGroups,
} from "@/app/types/sale-log-types";
import { Dealership, FinanceProduct } from "@/app/types/types";

export interface DealershipContext {
  currentDealership: Dealership | undefined;
  setCurrentDealership: (dealership: Dealership) => void;
  dealerships: Dealership[];
  setDealerships: (dealerships: Dealership[]) => void;
  hasFeature: (feature: string) => boolean;
  loading: boolean;
  // Why the dealership list couldn't be resolved, so the shell can explain the
  // problem instead of spinning forever. `no-access` means the fetch succeeded
  // but came back empty — the account exists but was never linked to a store.
  dealershipsError: "no-access" | "fetch-failed" | null;

  // User management functionality
  users: User[];
  userGroups: UserGroup[];
  sortedUserGroups: OrganizedUserGroups;
  salesPeople: string[];
  salesManagers: string[];
  financeManagers: string[];
  serviceAdvisors: string[];
  partsAdvisors: string[];
  usersLoading: boolean;
  usersError: string | null;
  refetchUsers: () => Promise<void>;
}

export type Lender = {
  name: string;
};

export const DealershipContext = createContext<DealershipContext>({
  currentDealership: {
    name: "",
    key: "",
    id: "",
    active: false,
    dealershipConfiguration: {
      backendGPUTarget: 0,
      backendGrossTarget: 0,
      businessDays: 0,
      commentNotificationEmailEnabled: false,
      createdTimestamp: "",
      dailyRecapEmailEnabled: false,
      dailyRecapEmailUserList: [],
      dueBillNotificationEmailEnabled: false,
      frontendGPUTarget: 0,
      grossChangeEmailUserList: [],
      grossChangeNotificationEmailEnabled: false,
      grossChangeThreshold: 0,
      houseDealsEnabled: false,
      lenders: [],
      models: {},
      products: [],
      salesGrossTarget: 0,
      specializedBusinessUnits: [],
      timezone: "America/Chicago",
      unitsSoldTarget: 0,
      useCustomVehicleDB: false,
      financeProducts: [],
      _id: "",
      isCustomFinanceEnabled: false,
    },
  },
  loading: false,
  dealershipsError: null,
  setCurrentDealership: () => {},
  dealerships: [],
  setDealerships: () => {},
  hasFeature: () => false,

  // Add defaults for new properties
  users: [],
  userGroups: [],
  sortedUserGroups: {},
  salesPeople: [],
  salesManagers: [],
  financeManagers: [],
  serviceAdvisors: [],
  partsAdvisors: [],
  usersLoading: false,
  usersError: null,
  refetchUsers: async () => {},
});
